import type { ReactNode } from "react";
import { useState } from "react";
import { ChevronDown, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type FilterChipProps = {
  label: string;
  value?: string | null;
  onClear?: () => void;
  children: ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
  testId?: string;
};

export function FilterChip({
  label, value, onClear, children, align = "start", className, testId,
}: FilterChipProps) {
  const [open, setOpen] = useState(false);
  const hasValue = value !== null && value !== undefined && value !== "";

  return (
    <div className={cn("inline-flex items-center", className)} data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={cn(
              "h-8 text-xs gap-1.5 pr-2",
              hasValue ? "border-primary/40 bg-primary/5 text-foreground" : "text-muted-foreground",
              onClear && hasValue && "rounded-r-none border-r-0",
            )}
          >
            <span className="text-muted-foreground">{label}</span>
            {hasValue ? <span className="font-medium">{value}</span> : null}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align={align} className="p-2 w-auto min-w-[14rem]">
          {children}
        </PopoverContent>
      </Popover>

      {onClear && hasValue ? (
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-8 w-7 rounded-l-none border-primary/40 bg-primary/5 text-muted-foreground hover:text-foreground"
          onClick={onClear}
          aria-label={`Clear ${label}`}
        >
          <X className="h-3 w-3" />
        </Button>
      ) : null}
    </div>
  );
}

export type FilterBarProps = {
  children: ReactNode;
  onAddFilter?: () => void;
  onClearAll?: () => void;
  hasActiveFilters?: boolean;
  className?: string;
};

export function FilterBar({
  children, onAddFilter, onClearAll, hasActiveFilters, className,
}: FilterBarProps) {
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <Filter className="h-3.5 w-3.5 text-muted-foreground" />
      {children}
      {onAddFilter ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 text-xs text-muted-foreground hover:text-foreground"
          onClick={onAddFilter}
        >
          + Filter
        </Button>
      ) : null}
      {hasActiveFilters && onClearAll ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 text-xs text-muted-foreground hover:text-foreground"
          onClick={onClearAll}
        >
          Clear all
        </Button>
      ) : null}
    </div>
  );
}

export default FilterBar;
